import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { NavChild, NavMenuEntry } from './navItems';

function isNavPathMatch(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function getBestChildTo(pathname: string, children: NavChild[]): string | null {
  let best: string | null = null;
  for (const c of children) {
    if (!isNavPathMatch(pathname, c.to)) {
      continue;
    }
    if (best === null || c.to.length > best.length) {
      best = c.to;
    }
  }
  return best;
}

export default function AppNavMenu(props: {
  entries: NavMenuEntry[];
  variant: 'dark' | 'light';
  expandedGroupLabels: ReadonlySet<string>;
  onToggleGroup: (label: string) => void;
  onNavigate?: () => void;
}): React.ReactElement {
  const { entries, variant, expandedGroupLabels, onToggleGroup, onNavigate } = props;
  const { pathname } = useLocation();
  const linkInactive = variant === 'dark' ? 'text-white-50' : 'text-body-secondary';
  const linkActive =
    variant === 'dark'
      ? 'active text-white fw-semibold bg-white bg-opacity-10'
      : 'active fw-semibold bg-primary text-white';
  const groupBtnClass =
    variant === 'dark'
      ? 'bg-transparent text-white-50 border-0 text-start w-100 py-2 px-3 d-flex align-items-center gap-2 text-decoration-none'
      : 'bg-transparent text-secondary border-0 text-start w-100 py-2 px-3 d-flex align-items-center gap-2 text-decoration-none';

  const renderChildLink = (child: NavChild, isActive: boolean) => (
    <NavLink
      key={child.to}
      to={child.to}
      end={child.to === '/'}
      className={`nav-link rounded py-2 px-3 ps-4 ${isActive ? linkActive : linkInactive}`}
      onClick={onNavigate}>
      {child.iconClass && <i className={`fas fa-fw ${child.iconClass} me-2`} />}
      {child.label}
    </NavLink>
  );

  return (
    <div className="py-2">
      {entries.map((entry) => {
        if (entry.type === 'link') {
          return (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.to === '/'}
              className={({ isActive }) =>
                `nav-link rounded px-3 py-2 ${isActive ? linkActive : linkInactive}`
              }
              onClick={onNavigate}>
              {entry.iconClass && <i className={`fas fa-fw ${entry.iconClass} me-2`} />}
              {entry.label}
            </NavLink>
          );
        }

        const bestChildTo = getBestChildTo(pathname, entry.children);
        const expanded = expandedGroupLabels.has(entry.label);
        return (
          <div key={entry.label}>
            <button
              type="button"
              className={groupBtnClass}
              onClick={() => onToggleGroup(entry.label)}
              aria-expanded={expanded}
              aria-controls={`nav-group-${entry.label.replace(/\s+/g, '-')}`}>
              {entry.iconClass && <i className={`fas fa-fw ${entry.iconClass}`} />}
              <span className="small text-uppercase fw-semibold flex-grow-1">{entry.label}</span>
              <i
                className={`fas fa-fw fa-chevron-${expanded ? 'down' : 'right'} small`}
                style={{ width: '1rem' }}
                aria-hidden
              />
            </button>
            {expanded ? (
              <nav
                id={`nav-group-${entry.label.replace(/\s+/g, '-')}`}
                className="nav flex-column pb-2">
                {entry.children.map((c) => renderChildLink(c, c.to === bestChildTo))}
              </nav>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
